// BrokerScreen — connect a buyer and a seller, earn a commission. THE zero-
// capital venture: working a deal costs energy, never cash, and the asset that
// compounds is REPUTATION — the close chance is shown up front and visibly
// climbs as the player's track record grows. Odds are knowable and improvable
// (expected value, not gambling). Chrome hype; lesson lines clear.

import {
  generateDeals,
  closeChance,
  networkLevel,
  WORK_ENERGY,
  type DealOffer,
} from '@biz-kid/engine';
import { useDispatch, useGameState, selectBroker } from '@biz-kid/store';
import { Card, Eyebrow, Button, Chip, Bar, money } from '../../ui/kit.js';

export function BrokerScreen({ onClose }: { onClose: () => void }) {
  const dispatch = useDispatch();
  const broker = useGameState(selectBroker);
  const reputation = useGameState((s) => s.profile.reputation);
  const energy = useGameState((s) => s.profile.happiness);
  const week = useGameState((s) => s.clock.week);
  const seed = useGameState((s) => s.rngSeed);

  const deals = generateDeals(seed, week);
  const chance = closeChance(reputation, broker.dealsClosed);
  const level = networkLevel(broker.dealsClosed);
  const workingThisWeek = broker.workingWeek === week && broker.workingSlot !== null;
  const lowEnergy = energy < WORK_ENERGY;

  return (
    <div className="bk-app" style={{ position: 'fixed', inset: 0, zIndex: 7, overflowY: 'auto' }}>
      <div style={{ maxWidth: 680, margin: '0 auto', padding: 'clamp(14px, 3vw, 26px)', paddingBottom: 90 }}>
        <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div>
            <Eyebrow color="var(--brand)">MONEY WITH NO MONEY</Eyebrow>
            <h1 style={{ margin: '2px 0 0', fontSize: 22, fontWeight: 900, color: 'var(--text)' }}>🤝 DEAL DESK</h1>
          </div>
          <Button tone="ghost" size="sm" onClick={onClose}>← HOME</Button>
        </header>

        {!broker.active ? (
          <Card>
            <p style={{ margin: 0, fontSize: 15, color: 'var(--text)', lineHeight: 1.6 }}>
              Someone needs a thing. Someone else HAS that thing. Connect them, and a slice of the deal is
              yours — no money needed to start, just hustle. Every deal you close builds your name, and your
              name closes the next deal.
            </p>
            <div style={{ marginTop: 16 }}>
              <Button tone="primary" size="lg" onClick={() => dispatch({ kind: 'StartBroker' })}>OPEN THE DESK →</Button>
            </div>
          </Card>
        ) : (
          <>
            {/* The broker's stats — reputation IS the asset */}
            <Card style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
                <Eyebrow>YOUR NAME IN THE STREETS</Eyebrow>
                <Chip tone="brand">{level.label.toUpperCase()}</Chip>
              </div>
              <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--text-2)' }}>CLOSE CHANCE</div>
                  <div className="bk-tnum" style={{ fontSize: 30, fontWeight: 900, color: 'var(--up)', lineHeight: 1.1 }}>
                    {Math.round(chance * 100)}%
                  </div>
                  <div style={{ marginTop: 6 }}>
                    <Bar frac={chance} color="var(--up)" height={8} />
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--text-2)' }}>DEALS CLOSED</div>
                  <div className="bk-tnum" style={{ fontSize: 30, fontWeight: 900, color: 'var(--text)', lineHeight: 1.1 }}>
                    {broker.dealsClosed}
                  </div>
                  {level.next !== null && (
                    <div style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 700 }}>
                      {level.next - broker.dealsClosed} to next level
                    </div>
                  )}
                </div>
              </div>
              <p style={{ margin: '10px 0 0', fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.5 }}>
                💡 Brokering needs no money — your reputation is the asset. Every closed deal raises it, and a
                higher reputation closes more deals. That's compounding, without spending a single coin.
              </p>
            </Card>

            {/* This week's board */}
            <Card>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
                <Eyebrow color="var(--brand)">THIS WEEK'S BOARD</Eyebrow>
                {workingThisWeek && <Chip tone="brand">⏳ DEAL IN PLAY — resolves at week's end</Chip>}
              </div>
              <div style={{ display: 'grid', gap: 10 }}>
                {deals.map((deal) => (
                  <DealRow
                    key={deal.slot}
                    deal={deal}
                    working={workingThisWeek && broker.workingSlot === deal.slot}
                    disabled={workingThisWeek || lowEnergy}
                    lowEnergy={lowEnergy}
                    onWork={() => dispatch({ kind: 'BrokerWork', slot: deal.slot })}
                  />
                ))}
              </div>
              <p style={{ margin: '12px 0 0', fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.5 }}>
                💡 You can work ONE deal a week, and it costs {WORK_ENERGY} energy — calls, legwork, follow-ups.
                The deal pays out (or doesn't) when the week wraps.
              </p>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function DealRow({
  deal, working, disabled, lowEnergy, onWork,
}: {
  deal: DealOffer;
  working: boolean;
  disabled: boolean;
  lowEnergy: boolean;
  onWork: () => void;
}) {
  return (
    <div
      style={{
        padding: 12, borderRadius: 14,
        border: working ? '1px solid var(--warn)' : '1px solid var(--border)',
        background: 'var(--surface-2)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ fontSize: 24, flexShrink: 0 }}>{deal.emoji}</span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontWeight: 800, fontSize: 14.5, color: 'var(--text)' }}>
            {deal.need}
          </span>
          <span style={{ display: 'block', fontSize: 12.5, color: 'var(--text-2)' }}>
            ↔ {deal.supply} · {money(deal.dealSize)} deal
          </span>
        </span>
        <span style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Your cut</div>
          <div className="bk-tnum" style={{ fontSize: 16, fontWeight: 900, color: 'var(--up)' }}>+{money(deal.commission)}</div>
        </span>
      </div>
      <div style={{ marginTop: 10 }}>
        {working ? (
          <Chip tone="brand">⏳ WORKING IT…</Chip>
        ) : (
          <Button tone="primary" size="sm" onClick={onWork} disabled={disabled}>
            {lowEnergy ? '😮‍💨 TOO TIRED' : '🤝 WORK THIS DEAL'}
          </Button>
        )}
      </div>
    </div>
  );
}
